import React, { useState, useEffect, useContext } from 'react';
import { Badge, Typography, Popover, List, Avatar, Button, Drawer, notification } from 'antd';
import {
    IconAlignRight,
    IconAddressBook,
    IconBell,
    IconTruckDelivery,
    IconReceipt2,
    IconAlertCircle,
    IconWallet
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import GlobalProfileMenu from '../../util/GlobalProfileMenu';
import handleLogout from '../../util/logout';
import { fromNow } from '../../util/dateUtils';
import "../../css/topNav.css";
import near from "../../assets/img/near.jpg";

import UserServ from '../../service/UserServ';
import moment from 'moment';

import { jwtDecode } from 'jwt-decode';
import { ThemeContext } from '../../util/ThemeContext';
import { useGlobalAuthModal } from '../../util/GlobalAuthModal';
import { usePreference } from '../../context/PreferenceContext';
import PreferenceSwitcher from '../PreferenceSwitcher';
import NotificationServ from '../../service/NotificationServ';
import { formatNotificationText } from '../../util/unitFormatter';

const { Text } = Typography;

const BuyerTopNav = ({ onToggle }) => {
    const navigate = useNavigate();
    const { darkMode } = useContext(ThemeContext);
    const { showAlert } = useGlobalAuthModal();
    const { formatPrice, t } = usePreference();
    const [stats, setStats] = useState({ messagesCount: 0, unreadNotifications: 0, firstName: "", lastName: "", credits: 0 });
    const [notifications, setNotifications] = useState([]);
    const [notifDrawerOpen, setNotifDrawerOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const fetchStats = async () => {
        try {
            const res = await UserServ.getBuyerStats();
            // Assuming res.data contains { firstName, lastName, ... }
            if (res && res.data) {
                setStats(res.data);
            }

            const notifRes = await UserServ.getNotifications({ limit: 5, role: 'user' });
            if (notifRes && notifRes.data) {
                setNotifications(notifRes.data);
            }
        } catch (error) {
            console.error("Failed to fetch buyer stats", error);
            // Fallback: try to get name from token if API fails
            const token = localStorage.getItem("customerToken") || localStorage.getItem("token");
            if (token) {
                try {
                    const decoded = jwtDecode(token);
                    setStats(prev => ({
                        ...prev,
                        firstName: decoded.firstName || decoded.name || decoded.fullName || ""
                    }));
                } catch (e) { }
            }
        }
    };

    useEffect(() => {
        // Initial name from token before API call finishes
        const token = localStorage.getItem("customerToken") || localStorage.getItem("token");
        if (token) {
            try {
                const decoded = jwtDecode(token);
                setStats(prev => ({
                    ...prev,
                    firstName: decoded.firstName || decoded.name || decoded.fullName || ""
                }));
            } catch (e) { }
        }
        fetchStats();

        window.addEventListener('profile-updated', fetchStats);
        window.addEventListener('notification-refresh', fetchStats);
        return () => {
            window.removeEventListener('profile-updated', fetchStats);
            window.removeEventListener('notification-refresh', fetchStats);
        };
    }, []);

    const handleLogOut = () => {
        showAlert({
            title: t('logout_title'),
            description: t('logout_description'),
            buttons: [
                { text: t('stay_here'), variant: 'default' },
                {
                    text: t('sign_out'),
                    variant: 'primary',
                    onClick: () => {
                        handleLogout();
                    }
                }
            ]
        });
    };

    const getIcon = (item) => {
        const type = item.type;
        const style = { borderRadius: 8 };
        const layoutClass = "pcu-padding-8 pcu-flex-center";

        switch (type) {
            case 'order':
                return <div style={style} className={`${layoutClass} pcu-bg-orange-light`}><IconTruckDelivery color="#FF8110" size={20} /></div>;
            case 'payment':
                return <div style={style} className={`${layoutClass} pcu-bg-green-light`}><IconReceipt2 color="#22c55e" size={20} /></div>;
            case 'system':
                return <div style={style} className={`${layoutClass} pcu-bg-blue-light`}><IconAlertCircle color="#3b82f6" size={20} /></div>;
            default:
                return <div style={style} className={`${layoutClass} pcu-bg-slate-light`}><IconBell color="#64748b" size={20} /></div>;
        }
    };

    const handleMarkAllRead = async () => {
        try {
            await UserServ.markAllNotificationsRead();
            fetchStats();
            window.dispatchEvent(new Event('notification-refresh'));
        } catch (error) {
            console.error("Failed to mark all as read", error);
        }
    };

    const handleNotificationClick = async (item) => {
        setNotifDrawerOpen(false);

        if (!item.isRead) {
            try {
                await NotificationServ.markAsRead(item._id);
                fetchStats();
                window.dispatchEvent(new Event("notification-refresh"));
            } catch (error) {
                console.error("Failed to mark as read", error);
            }
        }

        const lowerTitle = item.title?.toLowerCase() || "";
        const currentRole = localStorage.getItem("role")?.toLowerCase() || "user";

        if (item.orderId || lowerTitle.includes('order') || lowerTitle.includes('return') || lowerTitle.includes('cancel')) {
            const isReturn = lowerTitle.includes('return') || lowerTitle.includes('cancel') || lowerTitle.includes('refund');
            const isIssue = lowerTitle.includes('order update') || lowerTitle.includes('replace order');
            if (currentRole === 'seller') {
                navigate(isReturn ? '/seller/return-requests' : '/seller/orders', { state: { highlightOrderId: item.orderId } });
            } else if (['admin', 'superadmin'].includes(currentRole)) {
                navigate(isReturn ? '/admin/returns' : '/admin/dashboard');
            } else {
                navigate(isReturn ? '/buyer/return-requests' : '/buyer/orders', { 
                    state: { 
                        highlightOrderId: item.orderId,
                        ...(isIssue && { openIssueOrderId: item.orderId })
                    } 
                });
            }
            return;
        }
        if (lowerTitle.includes('seller request') || lowerTitle.includes('seller application')) {
            if (['admin', 'superadmin'].includes(currentRole)) navigate('/admin/seller-requests', { state: { requestId: item.metaData?.requestId } });
            else navigate(`/${currentRole}/dashboard`);
            return;
        }
        if (lowerTitle.includes('credit') || lowerTitle.includes('wallet')) {
            if (currentRole === 'user') navigate('/buyer/rewards/credits');
            else navigate(`/${currentRole}/dashboard`);
            return;
        }
        if (item.type === 'coupon' || lowerTitle.includes('coupon')) {
            navigate('/buyer/offers');
            return;
        }

        navigate(currentRole === 'seller' ? '/seller/dashboard' : (['admin', 'superadmin'].includes(currentRole) ? '/admin/dashboard' : '/buyer/dashboard'));
    };

    const notificationContent = (
        <div style={{ padding: isMobile ? '0' : '0 4px' }}>
            <div className="pcu-flex pcu-justify-between pcu-items-center pcu-margin-bottom-16">
                <Text strong className="pcu-font-18">{t('notifications')}</Text>
                <div className="pcu-flex pcu-gap-12">
                    <Button
                        type="link"
                        size="small"
                        onClick={handleMarkAllRead}
                        className="pcu-padding-x-0"
                    >
                        {t('mark_all_read')}
                    </Button>
                    <Button
                        type="link"
                        size="small"
                        onClick={() => {
                            const currentRole = localStorage.getItem("role")?.toLowerCase() || "user";
                            navigate(currentRole === 'seller' ? '/seller/notifications' : (['admin', 'superadmin'].includes(currentRole) ? '/admin/notifications' : '/buyer/notifications'));
                            setNotifDrawerOpen(false);
                        }}
                        className="pcu-padding-x-0"
                    >
                        {t('view_all')}
                    </Button>
                </div>
            </div>
            <List
                itemLayout="horizontal"
                dataSource={notifications}
                renderItem={item => (
                    <List.Item
                        onClick={() => handleNotificationClick(item)}
                        style={{
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--color-border-muted)',
                            background: !item.isRead 
                                ? ((item.title?.toLowerCase().includes('reject') || item.title?.toLowerCase().includes('fail')) ? '#fef2f2' : '#fff7ed') 
                                : 'transparent',
                        }}
                        className="pcu-padding-y-12 pcu-padding-x-12 pcu-radius-8 pcu-margin-bottom-4"
                    >
                        <List.Item.Meta
                            avatar={getIcon(item)}
                            title={<Text strong={!item.isRead} className="pcu-font-14">{formatNotificationText(item.title)}</Text>}
                            description={
                                <div className="pcu-font-12">
                                    <div className="pcu-color-muted pcu-margin-bottom-2" dangerouslySetInnerHTML={{ __html: formatNotificationText(item.description) }}></div>
                                    <Text type="secondary" className="pcu-font-11">{fromNow(item.createdAt)}</Text>
                                </div>
                            }
                        />
                    </List.Item>
                )}
            />
            {notifications.length === 0 && (
                <div className="pcu-text-center pcu-padding-y-40 pcu-color-muted">{t('no_notifications')}</div>
            )}
        </div>
    );

    return (
        <div className={`topnav ${darkMode ? "dark" : "light"}`}>
            <div className="topnav-left">
                <button onClick={onToggle} className="menu-btn">
                    <IconAlignRight size={22} />
                </button>
                {!isMobile && (
                    <img
                        src={near}
                        alt="Nearzon"
                        className="logo-img pcu-margin-left-10"
                        onClick={() => navigate("/")}
                    />
                )}
            </div>

            <div className="topnav-right">
                <div className="desktop-only-flex" style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: isMobile ? '8px' : '16px',
                    marginRight: '8px'
                }}>
                    <PreferenceSwitcher mode={darkMode ? "dark" : "light"} />
                    <div className="credits-display"
                        onClick={() => navigate('/buyer/rewards/credits')}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: darkMode ? 'rgba(255, 255, 255, 0.05)' : '#fff7ed',
                            padding: '0 12px',
                            height: '32px',
                            borderRadius: '20px',
                            cursor: 'pointer',
                            whiteSpace: 'nowrap',
                            border: '1px solid',
                            borderColor: darkMode ? 'rgba(255, 255, 255, 0.1)' : '#fed7aa',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            boxShadow: '0 2px 4px var(--color-bg-shadow)'
                        }}>
                        <IconWallet size={20} color="var(--nearzon-orange)" />
                        <Text strong style={{ color: darkMode ? 'var(--color-text-title)' : '#9a3412', fontSize: '14px', fontWeight: '700', marginTop: "10px" }}>
                            {formatPrice(stats.credits || 0)}
                        </Text>
                    </div>
                    <div className="nav-icon-badge" onClick={() => navigate('/buyer/contact')}>
                        <Badge count={stats.messagesCount} showZero={false}>
                            <IconAddressBook size={22} color="#64748b" className="pcu-cursor-pointer" />
                        </Badge>
                    </div>
                    <div className="nav-icon-badge" onClick={() => setNotifDrawerOpen(true)}>
                        <Badge
                            count={stats.unreadNotifications}
                            showZero={false}
                            className="pcu-cursor-pointer"
                        >
                            <IconBell
                                size={22}
                                color="#64748b"
                            />
                        </Badge>
                    </div>
                </div>
                <GlobalProfileMenu handleLogOut={handleLogOut} stats={stats} />
            </div>

            <Drawer
                title={null}
                placement="right"
                onClose={() => setNotifDrawerOpen(false)}
                open={notifDrawerOpen}
                width={isMobile ? '85%' : 380}
                bodyStyle={{ padding: '24px' }}
            >
                {notificationContent}
            </Drawer>
        </div>
    );
};

export default BuyerTopNav;
